import React from 'react';
import { useSearchParams } from 'react-router-dom';
import TdButton from './Button';
import { byJob, connectionLabel, isNpc, isOnline, presenceOf, roleLabel } from './model/derive';
import { formatMemberHours, tzShortName } from './model/time';
import { useWorkboardCtx } from './model/useWorkboard';

/**
 * 팀원 상태 (MVP §10): 진행 중 / offline / 이탈.
 * offline 은 이탈이 아니다 — 작업 시간을 함께 보여줘 언제 답이 올지 알 수 있게 한다.
 */
const TeamPanel = () => {
  const { data, memberById, now, displayTz, viewer, readOnly } = useWorkboardCtx();
  const [, setParams] = useSearchParams();
  const members = [...memberById.values()]
    .filter((m) => !isNpc(m))
    .sort(byJob);
  const tz = tzShortName(displayTz, now);
  const canMessage = !readOnly && !!viewer && !isNpc(viewer) && viewer.status !== 'dropped';
  const setMessaging = (on) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (on) next.set('organizer', '1');
        else next.delete('organizer');
        return next;
      },
      { replace: true },
    );

  return (
    <section aria-label='Team' className='flex flex-col gap-3'>
      <h2 className='typo-b16Strong text-text-primary'>Team</h2>
      <ul className='flex flex-col rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
        {members.map((m) => {
          const p = presenceOf(m, now);
          return (
            <li
              key={m.id}
              className='flex flex-col gap-0.5 border-b border-green-deep03/[0.08] px-4 py-3 last:border-b-0'
            >
              <div className='flex justify-between gap-2 text-[14px] leading-[140%]'>
                <span className='truncate font-medium text-text-primary'>{m.name}</span>
                <span className='shrink-0 text-text-secondary'>{roleLabel(m)}</span>
              </div>
              {/* 점 = 실제 접속 (Online · Offline), 아래 줄 = 작업 시간 밖이면 Off hours */}
              {p !== 'dropped' && (
                <span
                  className={
                    isOnline(m, now, data.viewerId)
                      ? 'flex items-center gap-1.5 text-[13px] leading-[140%] text-functional-success04'
                      : 'flex items-center gap-1.5 text-[13px] leading-[140%] text-text-secondary'
                  }
                >
                  <span
                    className={
                      isOnline(m, now, data.viewerId)
                        ? 'h-1.5 w-1.5 shrink-0 rounded-full bg-functional-success03'
                        : 'h-1.5 w-1.5 shrink-0 rounded-full bg-grayscale-gray04'
                    }
                  />
                  {connectionLabel(m, now, data.viewerId)}
                </span>
              )}
              {p === 'offline' && (
                <span className='pl-3 text-[12px] leading-[140%] text-text-secondary'>
                  Off hours · works {formatMemberHours(m, displayTz, now)} {tz}
                </span>
              )}
              {p === 'dropped' && (
                <span className='flex items-center gap-1.5 text-[13px] leading-[140%] text-text-tertiary'>
                  <span className='h-1.5 w-1.5 rounded-full bg-grayscale-gray06' />
                  Dropped
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {canMessage && (
        <div className='flex flex-col gap-3 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-4 py-3.5'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-[14px] font-medium leading-[140%] text-text-primary'>Organizer</span>
            <p className='text-[13px] leading-[145%] text-text-secondary'>
              Stuck, or something’s off? Only the organizers see your message.
            </p>
          </div>
          <TdButton variant='soft' size='s' onClick={() => setMessaging(true)}>
            Message the organizer
          </TdButton>
        </div>
      )}
    </section>
  );
};

export default TeamPanel;
